import { useMemo } from "react";
import type { TProject } from "@/zapi-frontend/stores/project";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import { EMPTY_VALUE_TOKEN, HAS_VALUE_TOKEN, MODIFIED_BY_FILTER_KEY, OWNER_FILTER_KEY, STATUS_FILTER_KEY } from "../const/const";
import type { TActiveFilters, TPortfolioSortDir, TPortfolioSortField } from "./portfolioStorage";
import { getProjectStatus } from "./projectStatus";

function matchesSearch(p: TProject, q: string): boolean {
  if (!q.trim()) return true;
  const s = q.trim().toLowerCase();
  const name = (p.name || "").toLowerCase();
  return name.includes(s);
}

function matchesFilters(p: TProject, filters: TActiveFilters): boolean {
  const vals = p.dimensionValues ?? {};
  for (const [dimId, selected] of Object.entries(filters)) {
    if (selected.size === 0) continue;
    if (dimId === STATUS_FILTER_KEY) {
      const status = getProjectStatus(p).name;
      if (!selected.has(status)) return false;
      continue;
    }
    if (dimId === OWNER_FILTER_KEY || dimId === MODIFIED_BY_FILTER_KEY) {
      const person = (dimId === OWNER_FILTER_KEY ? p.createdBy : p.modifiedBy) || "";
      if (!selected.has(person === "" ? EMPTY_VALUE_TOKEN : person)) return false;
      continue;
    }
    const v = vals[dimId] ?? "";
    if (selected.has(HAS_VALUE_TOKEN)) {
      if (v === "") return false;
      continue;
    }
    if (v === "") {
      if (!selected.has(EMPTY_VALUE_TOKEN)) return false;
    } else if (!selected.has(v)) {
      return false;
    }
  }
  return true;
}

export function usePortfolioDerivedList(
  projects: TProject[],
  search: string,
  filters: TActiveFilters,
  sortField: TPortfolioSortField,
  sortDir: TPortfolioSortDir
) {
  return useMemo(() => {
    const filtered = projects.filter((p) => matchesSearch(p, search) && matchesFilters(p, filters));
    if (sortField !== "none") {
      const mult = sortDir === "asc" ? 1 : -1;
      filtered.sort((a, b) => {
        let cmp = 0;
        if (sortField === "name") {
          cmp = (a.name || "").localeCompare(b.name || "");
        } else if (sortField === "createdBy") {
          cmp = (a.createdBy || "").localeCompare(b.createdBy || "");
        } else if (sortField === "modifiedBy") {
          cmp = (a.modifiedBy || "").localeCompare(b.modifiedBy || "");
        } else if (sortField === "lastModified") {
          cmp = (a.lastModified || "").localeCompare(b.lastModified || "");
        } else if (sortField === "status") {
          const rank = { Framing: 0, Generating: 1, Complete: 2 } as const;
          cmp = rank[getProjectStatus(a).name] - rank[getProjectStatus(b).name];
        } else if (sortField.kind === "dimension") {
          const id = sortField.dimensionId;
          cmp = ((a.dimensionValues ?? {})[id] || "").localeCompare((b.dimensionValues ?? {})[id] || "");
        }
        return cmp * mult;
      });
    }
    return filtered;
  }, [projects, search, filters, sortField, sortDir]);
}

export function distinctPeopleValues(projects: TProject[], field: "createdBy" | "modifiedBy"): string[] {
  const set = new Set<string>();
  let hasEmpty = false;
  for (const p of projects) {
    const v = p[field] || "";
    if (v === "") hasEmpty = true;
    else set.add(v);
  }
  const sorted = [...set].sort((x, y) => x.localeCompare(y));
  if (hasEmpty) sorted.push(EMPTY_VALUE_TOKEN);
  return sorted;
}

export function distinctValuesForDimension(
  projects: TProject[],
  dimensionId: string,
  dim: TOrganizationProjectDimension | undefined
): string[] {
  const set = new Set<string>();
  for (const p of projects) {
    const v = (p.dimensionValues ?? {})[dimensionId] ?? "";
    if (v === "") set.add(EMPTY_VALUE_TOKEN);
    else set.add(v);
  }
  if (dim?.type === "dropdown" && dim.options?.length) {
    const ordered = [...dim.options];
    const inOptions = new Set(ordered);
    for (const v of set) {
      if (v === EMPTY_VALUE_TOKEN) continue;
      if (!inOptions.has(v)) {
        ordered.push(v);
        inOptions.add(v);
      }
    }
    if (set.has(EMPTY_VALUE_TOKEN)) ordered.push(EMPTY_VALUE_TOKEN);
    return ordered;
  }
  const hasEmpty = set.delete(EMPTY_VALUE_TOKEN);
  const sorted = [...set].sort((x, y) => x.localeCompare(y));
  if (hasEmpty) sorted.push(EMPTY_VALUE_TOKEN);
  return sorted;
}
